import React, { useEffect } from 'react';
import { Add } from '@mui/icons-material';
import { Button, MenuItem, Typography } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { updateOrgMember } from 'js/actions';
import { UPDATE_ORG_MEMBER_REQUEST, UPDATE_USER_INFO_REQUEST } from 'js/constants/actionTypes';
import { USER_ROLE_OPTIONS } from 'js/constants/userRoles';
import { isValidEmail } from 'js/lib/ValidationUtils';
import { getCurrentOrg } from 'js/reducers/selectors';
import { AdminDurationSelect } from 'new/components/settings/Members/AdminDurationSelect';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { Select } from 'new/elements/Select';
import { TextField } from 'new/elements/TextField';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { useModals } from 'new/hooks/useModals';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { ModalComponent } from 'new/routes/RouterManager';
import { showAlert } from 'new/stores/showAlert/action';

import styles from './Members.module.scss';

interface AddUserProps {
  /** Target org to add members to. Defaults to the current user's active org — pass this
   * explicitly when opening the modal from a context (e.g. an admin tool) that manages a
   * different org than the one the signed-in user is currently in. */
  orgId?: number;
  /** Shown in the modal title as "Add team member to {orgName}" when provided, so it's
   * clear which org is being edited when this isn't the caller's own org. */
  orgName?: string;
  onSuccess?: () => void;
}

const Role = (item: (typeof USER_ROLE_OPTIONS)[number]) => {
  return (
    <MenuItem key={item.value} className={styles.selectOption} value={item.value}>
      <Typography>{item.title}</Typography>
      <Typography variant="caption">{item.description}</Typography>
    </MenuItem>
  );
};

export const AddUser: ModalComponent = (props: AddUserProps & ModalOutProps) => {
  const { setModalActions, setLoading, setTitle, orgId, orgName, onSuccess } = props;
  const { closeModal } = useModals();
  const dispatch: any = useDispatch();
  const currentOrg = useSelector(getCurrentOrg);
  const targetOrgId = orgId ?? currentOrg.id;
  const isLoading = useSelector(
    (state: any) =>
      !!state.request[UPDATE_ORG_MEMBER_REQUEST] || !!state.request[UPDATE_USER_INFO_REQUEST],
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  });
  const { fields, append, remove } = useFieldArray({
    name: 'members',
    rules: {
      minLength: 1,
    },
    control,
  });

  useDidUpdateEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const onConfirm = (data) => {
    const members = data.members.map((m) => {
      const member: any = {
        full_name: m.full_name,
        email: m.email,
        access_role: m.access_role,
      };
      if (m.access_role_expiration_seconds != null) {
        member.access_role_expiration_seconds = m.access_role_expiration_seconds;
      }
      return member;
    });

    dispatch(updateOrgMember(targetOrgId, members))
      .then(() => {
        dispatch(
          showAlert({
            title: 'User(s) added successfully!',
            severity: 'success',
          }),
        );
        onSuccess?.();
        closeModal(AddUser.modalConfig.name);
      })
      .catch(() => {
        dispatch(
          showAlert({
            title: 'Not able to add users',
            severity: 'error',
          }),
        );
      });
  };

  useEffect(() => {
    onAddEntry();
    if (orgName) {
      setTitle(`Add team member to ${orgName}`);
    }
    setModalActions({
      ok: {
        text: 'Invite users',
        onClick: handleSubmit(onConfirm),
      },
    });
  }, []);

  const onAddEntry = () => {
    append({
      full_name: '',
      email: '',
      access_role: 'member',
      access_role_expiration_seconds: null,
    });
  };

  return (
    <div className={styles.addUserContainer}>
      {fields.map((field, i) => (
        <div key={field.id} className={styles.fieldSet}>
          <TextField
            className={styles.name}
            label="Name"
            error={!!errors?.members?.[i]?.full_name}
            helperText={
              !!errors?.members?.[i]?.full_name
                ? errors?.members?.[i]?.full_name?.message
                : undefined
            }
            {...register(`members.${i}.full_name`, {
              required: 'Please input user full name',
            })}
          />
          <TextField
            className={styles.email}
            type="email"
            error={!!errors?.members?.[i]?.email}
            helperText={
              !!errors?.members?.[i]?.email ? errors?.members?.[i]?.email?.message : undefined
            }
            label="Email"
            {...register(`members.${i}.email`, {
              required: 'Please, input the user email',
              validate: (email) => {
                return isValidEmail(email) || 'Email is not valid';
              },
            })}
          />
          <div className={styles.rowContainer}>
            <Select
              label="Role"
              fullWidth
              defaultValue="member"
              items={USER_ROLE_OPTIONS}
              renderItem={Role}
              error={!!errors?.members?.[i]?.access_role}
              helperText={
                !!errors?.members?.[i]?.access_role
                  ? errors?.members?.[i]?.access_role?.message
                  : undefined
              }
              renderValue={(value) => (
                <Typography>
                  {USER_ROLE_OPTIONS.find((role) => role.value === value)?.title}
                </Typography>
              )}
              {...register(`members.${i}.access_role`, {
                required: 'Please select a role for the user',
              })}
            />
            {i !== 0 ? (
              <Button
                className={styles.removeRowButton}
                variant="outlined"
                color="secondary"
                onClick={() => remove(i)}
              >
                <DeleteIcon width={16} height={16} />
              </Button>
            ) : null}
          </div>
          <AdminDurationSelect
            control={control}
            setValue={setValue}
            roleFieldName={`members.${i}.access_role`}
            expirationFieldName={`members.${i}.access_role_expiration_seconds`}
            label="Select duration"
          />
        </div>
      ))}

      <div className={styles.add}>
        <Button onClick={onAddEntry} color="primary" startIcon={<Add />} variant="text">
          Add another team member
        </Button>
      </div>
    </div>
  );
};

AddUser.modalConfig = {
  name: 'members.AddUser',
  title: 'Add team member',
};
